import axios, { AxiosProgressEvent } from 'axios';

import * as DigitalCheckIn from './models/digitalCheckIn';
import * as Log from './models/log';
import * as Patient from './models/patient';
import * as PatientCallbackRequest from './models/patientCallbackRequest';
import * as PrevisitFormTemplate from './models/previsit/formTemplate';
import * as Timeline from './models/timeline';
import * as Workflow from './models/workflow';

import { Common } from './common';
import { Sex, SuccessResponse } from './models/common';
import {
  EncounterData,
  HasEncounterOrPrevisitFormSetId,
} from './models/encounter';
import { NoteFieldWithConfig } from './models/note/field';
import { PatientNotification } from './models/patient/notification';
import { ThemeResponse } from './models/practice/theme';
import {
  PatientData,
  ProviderAssetSignedUrls,
  FindAllOptions,
} from './models/previsit';
import { PrevisitFormFieldWithConfig } from './models/previsit/formField';
import {
  PrevisitFormSetsResponse,
  PrevisitFormSetWithFormsAndAuthCode,
} from './models/previsit/formSet';
import {
  DisplayablePrevisitFormTemplate,
  PrevisitFormTemplateWithParametersAndFieldConfigs,
} from './models/previsit/formTemplate';
import {
  BasePrevisitFormTemplateFieldConfig,
  PrevisitFormTemplateFieldConfig,
  PrevisitFormTemplateFieldConfigWithConditions,
  UpsertValues as UpsertFieldConfig,
} from './models/previsit/formTemplateFieldConfig';
import { Language } from './models/previsit/language';
import {
  ContactVerificationSource,
  ProvisionalFormSetData,
  ReconciliationStatus,
  RegistrationContactVerification,
} from './models/previsit/register';
import { AuthPatientBody } from './models/previsit/auth/patient';
import { ProvisionalPatient } from './models';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  // admin endpoints
  async fetchPrevisitFormTemplates(
    options: FindAllOptions = {},
  ): Promise<{ previsitFormTemplates: DisplayablePrevisitFormTemplate[] }> {
    const url = this.common.constructPath(`/previsit/form-templates`, options);
    return this.common.getJSON(url);
  }

  async createFormTemplate(
    createValues: PrevisitFormTemplate.CreateValues,
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplateWithLocalizations> {
    const url = this.common.constructPath('/previsit/form-templates');
    return this.common.postJSON(url, createValues);
  }

  async createFormTemplatePreview(
    formTemplateId: string,
    templateFieldConfigs: BasePrevisitFormTemplateFieldConfig[],
  ) {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}/preview`,
    );
    return this.common.postJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async updateFormTemplate(
    formTemplateId: string,
    updateValues: PrevisitFormTemplate.UpdateValues,
  ): Promise<{
    template: PrevisitFormTemplate.PrevisitFormTemplateWithLocalizations;
  }> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}`,
    );
    return this.common.patchJSON(url, updateValues);
  }

  async createFormTemplateFieldConfigs(
    formTemplateId: string,
    templateFieldConfigs: BasePrevisitFormTemplateFieldConfig[],
  ): Promise<PrevisitFormTemplateFieldConfig[]> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}/field-configs`,
    );
    return this.common.postJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async updateFormTemplateFieldConfig(
    formTemplateId: string,
    formTemplateFieldConfigId: string,
    updateValues: Partial<BasePrevisitFormTemplateFieldConfig>,
  ): Promise<PrevisitFormTemplateFieldConfig> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}/field-configs/${formTemplateFieldConfigId}`,
    );
    return this.common.patchJSON(url, updateValues);
  }

  async updateFormTemplateFieldConfigs(
    formTemplateId: string,
    templateFieldConfigs: UpsertFieldConfig[],
  ): Promise<PrevisitFormTemplateFieldConfigWithConditions[]> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}/field-configs`,
    );
    return this.common.putJSON(url, {
      fields: templateFieldConfigs,
    });
  }

  async deleteFormTemplate(id: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`/previsit/form-templates/${id}`);
    return this.common.deleteJSON(url);
  }

  async deleteFormTemplateLanguage(
    id: string,
    languageId: string,
  ): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${id}/languages/${languageId}`,
    );
    return this.common.deleteJSON(url);
  }

  async fetchAllLabels(): Promise<string[]> {
    const url = this.common.constructPath('/previsit/form-templates/labels');
    return this.common.getJSON(url);
  }

  // analyst app endpoints
  async fetchPrevisitNoteFields(encounterId: string): Promise<{
    hpi: NoteFieldWithConfig[];
    previsit: NoteFieldWithConfig[];
  }> {
    const url = this.common.constructPath(
      `/previsit/encounters/${encounterId}/note-sections`,
    );
    return this.common.getJSON(url);
  }

  async sendAll(
    formSetId: string,
    checkRateLimiter: boolean,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/previsit/form-sets/${formSetId}/sendAll`,
    );
    return this.common.postJSON(url, { checkRateLimiter });
  }

  async sendTextMessage(
    formSetId: string,
    checkRateLimiter: boolean,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/previsit/form-sets/${formSetId}/message`,
    );
    return this.common.postJSON(url, { checkRateLimiter });
  }

  async sendEmailMessage(
    formSetId: string,
    checkRateLimiter: boolean,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/previsit/form-sets/${formSetId}/email`,
    );
    return this.common.postJSON(url, { checkRateLimiter });
  }

  async callVoice(
    formSetId: string,
    checkRateLimiter: boolean,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      `/previsit/form-sets/${formSetId}/call`,
    );
    return this.common.postJSON(url, { checkRateLimiter });
  }

  // Deprecate
  async fetchFormByEncounterId(args: {
    encounterId: string;
    includeAuthenticationCode?: boolean;
    includeFieldHistory?: boolean;
  }): Promise<PrevisitFormSetWithFormsAndAuthCode> {
    const { encounterId, includeAuthenticationCode, includeFieldHistory } =
      args;
    const url = this.common.constructPath(
      `/previsit/encounters/${encounterId}/form-sets`,
      { includeAuthenticationCode, includeFieldHistory },
    );
    return this.common.getJSON(url);
  }

  async fetchAllFormSetsByEhrInformationOrEncounterId(args: {
    encounterId?: string;
    ehrInformationId?: string;
    includeAuthenticationCode?: boolean;
  }): Promise<PrevisitFormSetWithFormsAndAuthCode[]> {
    const url = this.common.constructPath(`/previsit/form-sets`, args);
    const { previsitFormSets } = await this.common.getJSON(url);
    return previsitFormSets;
  }

  async fetchFrontDeskAuthCode(
    patientId: string,
  ): Promise<{ code: string | null }> {
    const url = this.common.constructPath(
      `/previsit/patients/${patientId}/previsit-auth-code`,
    );
    return this.common.getJSON(url);
  }

  async fetchDigitalCheckInAuthCode(
    patientId: string,
    encounterId: string,
  ): Promise<{ code: string | null }> {
    const url = this.common.constructPath(
      `/previsit/patients/${patientId}/digital-check-in-auth-code`,
      { encounterId },
    );
    return this.common.getJSON(url);
  }

  async fetchEncounterPrevisitUrl(
    encounterId: string,
  ): Promise<{ encounterPrevisitUrl: string | null }> {
    const url = this.common.constructPath(
      `/previsit/encounters/${encounterId}/previsit-url`,
    );
    return this.common.getJSON(url);
  }

  async fetchPatientNotifications(
    formSetId: string,
    includeContent?: boolean,
  ): Promise<PatientNotification[]> {
    const url = this.common.constructPath(
      `/previsit/form-sets/${formSetId}/patient-notifications`,
      { includeContent },
    );
    return this.common.getJSON(url);
  }

  async fetchScheduledNotifications(formSetId?: string): Promise<
    {
      notificationConfigId: string;
      name: string;
      modality: string;
      activeTimestamp: string;
      notification?: PatientNotification;
    }[]
  > {
    const url = this.common.constructPath(
      `/previsit/form-sets/${formSetId}/scheduled-notifications`,
    );
    return this.common.getJSON(url);
  }

  async fetchTimelineEvents(
    args: HasEncounterOrPrevisitFormSetId,
  ): Promise<Timeline.Event[]> {
    const url = this.common.constructPath(`/previsit/timeline`, args);
    return this.common.getJSON(url);
  }

  async exportForm(encounterId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`/previsit/export/form`);
    return this.common.postJSON(url, { encounterId });
  }

  async createWorkflow(
    encounterId: string,
    status: Workflow.Previsit.Status,
  ): Promise<Workflow.Previsit.Status> {
    const url = this.common.constructPath(
      `/previsit/encounters/${encounterId}/workflows`,
    );
    const workflow = await this.common.postJSON(url, { status });
    return workflow.status;
  }

  async fetchPrevisitFormTemplate(
    formTemplateId: string,
    parseParameters?: boolean,
  ): Promise<PrevisitFormTemplateWithParametersAndFieldConfigs> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}`,
      parseParameters ? { parseParameters } : undefined,
    );
    return this.common.getJSON(url);
  }

  async createForm(
    encounterId: string,
    formTemplates: {
      [formTemplateId: string]: PrevisitFormTemplate.Parameters;
    },
  ): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `/previsit/encounters/${encounterId}/form-sets`,
    );
    return this.common.postJSON(url, {
      formTemplates,
    });
  }

  async deleteForm(previsitFormId: string): Promise<{
    removed: { previsitFormId: string; previsitFormFieldTotal: number };
  }> {
    const url = this.common.constructPath(`/previsit/forms/${previsitFormId}`);
    return this.common.deleteJSON(url);
  }

  async createAssetSignedUrl({
    fileName,
    configKey,
    contentType,
    override = false,
  }: {
    fileName: string;
    configKey: string;
    contentType?: string;
    override?: boolean;
  }): Promise<{ signedUrl: string; publicUrl: string }> {
    const url = this.common.constructPath(`/previsit/assets/upload-url`);
    return this.common.postJSON(url, {
      fileName,
      override,
      contentType,
      configKey,
    });
  }

  async createProviderAssetSignedUrls(
    fileNames: string[],
    override: boolean = false,
  ): Promise<ProviderAssetSignedUrls[]> {
    const url = this.common.constructPath(`/previsit/assets/upload-urls`);
    return this.common.postJSON(url, { fileNames, override });
  }

  async uploadAsset({
    file,
    signedUploadURL,
    onUploadProgress,
    contentType = 'image/png',
  }: {
    file: Blob;
    signedUploadURL: string;
    onUploadProgress?: (event: AxiosProgressEvent) => void;
    contentType?: string;
  }): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: { 'Content-Type': contentType },
    });
    return response.status;
  }

  // previsit web app endpoints
  async createPrevisitLog(args: {
    auditLog: Log.PrevisitAuditAction;
    previsitLog: Log.PrevisitAction;
    authenticationCode?: string;
    pathName?: string;
    source?: string;
  }): Promise<SuccessResponse> {
    const { auditLog, previsitLog, authenticationCode, pathName, source } =
      args;
    const url = this.common.constructPath('/patient-previsit/log');
    return this.common.postJSON(url, {
      auditLog,
      previsitLog,
      authenticationCode,
      pathName,
      source,
    });
  }

  async unsubscribeEmail(unsubscribeCode: string): Promise<SuccessResponse> {
    const url = this.common.constructPath('/patient-previsit/unsubscribe');
    return this.common.postJSON(url, {
      code: unsubscribeCode,
    });
  }

  async subscribeEmail(unsubscribeCode: string): Promise<SuccessResponse> {
    const url = this.common.constructPath('/patient-previsit/subscribe');
    return this.common.postJSON(url, { code: unsubscribeCode });
  }

  async fetchFormSets(
    authenticationCode: string,
    lang: Language = Language.En,
    future?: boolean,
    patientInfo?: boolean,
  ): Promise<PrevisitFormSetsResponse> {
    const url = this.common.constructPath(`/patient-previsit/form-sets`, {
      authenticationCode,
      lang: lang !== Language.En ? lang : undefined, // so we avoid joining localization tables when not required
      v2: true,
      future,
      patientInfo,
    });
    return this.common.getJSON(url, {
      headers: { 'cache-control': 'no-cache' },
    });
  }

  async fetchEncounter(authenticationCode: string): Promise<EncounterData> {
    const url = this.common.constructPath(`/patient-previsit/encounter`, {
      authenticationCode,
    });
    return this.common.getJSON(url);
  }

  async fetchPatient(authenticationCode: string): Promise<PatientData> {
    const url = this.common.constructPath(`/patient-previsit/patient`, {
      authenticationCode,
    });
    return this.common.getJSON(url);
  }

  getPassbookUrl(formSetId: string): string {
    return this.common.constructPath(`/patient-previsit/passbook/${formSetId}`);
  }

  getPayPassUrl(formSetId: string): string {
    return this.common.constructPath(`/patient-previsit/paypass/${formSetId}`);
  }

  async updateFormField(formSetId: string, formFieldId: string, value: string) {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/fields/${formFieldId}`,
    );
    return await this.common.patchJSON(url, { value });
  }

  async updateFormFields(
    formSetId: string,
    fields: PrevisitFormFieldWithConfig[],
  ): Promise<void> {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/fields`,
    );
    await this.common.patchJSON(url, { fields });
  }

  async startFormSet(formSetId: string, authenticationCode: string) {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/start`,
      { authenticationCode },
    );
    try {
      await this.common.postJSON(url, {});
    } catch (err) {
      console.warn(`form ${formSetId} has already been started`);
    }
  }

  async submitFormSet(formSetId: string, authenticationCode: string) {
    const url = this.common.constructPath(
      `/patient-previsit/form-sets/${formSetId}/submit`,
      { authenticationCode },
    );
    try {
      await this.common.postJSON(url, {});
    } catch (err) {
      console.warn(`form ${formSetId} has already been submitted`);
    }
  }

  async startForm(formId: string) {
    const url = this.common.constructPath(
      `/patient-previsit/forms/${formId}/started`,
    );
    return this.common.postJSON(url, {});
  }

  async completeForm(formId: string) {
    const url = this.common.constructPath(
      `/patient-previsit/forms/${formId}/completed`,
    );
    return this.common.postJSON(url, {});
  }

  async updateFormScore({
    formId,
    score,
    scores,
  }: {
    formId: string;
    score: number;
    scores: Record<string, number>;
  }) {
    const url = this.common.constructPath(
      `/patient-previsit/forms/${formId}/score`,
    );
    return this.common.postJSON(url, { score, scores });
  }

  async getLoginChallenge(
    authenticationCode: string,
    lastName: string,
    dateOfBirth: string,
  ): Promise<{ zips: string[] }> {
    const url = this.common.constructPath('/patient-previsit/auth/challenge', {
      authenticationCode,
    });
    return this.common.postJSON(url, { lastName, dateOfBirth });
  }

  async getContacts(args: {
    authenticationCode: string;
    dateOfBirth: string;
    lastName: string;
    zip?: string;
  }): Promise<{ email: string | null; phoneNumber: string | null }> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/contacts',
      args,
    );
    return this.common.getJSON(url);
  }

  async generateCode(args: {
    authenticationCode: string;
    dateOfBirth: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    zip?: string;
  }): Promise<boolean> {
    const url = this.common.constructPath('/patient-previsit/auth/code');
    const { success } = await this.common.postJSON(url, args);
    return success;
  }

  async maxLoginsExceeded(authenticationCode: string): Promise<boolean> {
    const url = this.common.constructPath('/patient-previsit/auth/limit', {
      authenticationCode,
    });
    const { limitReached } = await this.common.getJSON(url);
    return limitReached;
  }

  async isAuthenticated(): Promise<boolean> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/is-authenticated',
    );
    const { isAuthenticated } = await this.common.getJSON(url);
    return isAuthenticated;
  }

  async login(args: {
    authenticationCode: string;
    lastName: string;
    dateOfBirth: string;
    zip?: string;
  }): Promise<{ patient: Patient.Patient }> {
    const url = this.common.constructPath('/patient-previsit/auth/login');
    return this.common.postJSON(url, args);
  }

  async preAuthLogin(
    authenticationCode: string,
    authBypassCode: string,
  ): Promise<{ patient: Patient.Patient }> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/pre-auth-login',
    );
    return this.common.postJSON(url, {
      authenticationCode,
      authBypassCode,
    });
  }

  async receiptLogin(
    authenticationCode: string,
    lastName: string,
    dateOfBirth: Date,
  ): Promise<{ patient: Patient.Patient }> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/receipt-login',
    );
    return this.common.postJSON(url, {
      authenticationCode,
      lastName,
      dateOfBirth,
    });
  }

  async register(payload: {
    registrationCode: string;
    patientCode?: string;
    domain: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    cellPhoneNumber?: string;
    zipcode?: string;
    email?: string;
    sex?: Sex;
    practiceId?: string;
    previsitFormTemplateFieldConfigId?: string;
    provisionalPatientId?: string;
  }): Promise<
    | {
        authenticationCode: string;
      }
    | RegistrationContactVerification
  > {
    const url = this.common.constructPath(
      '/patient-previsit/auth/register/login',
    );
    return this.common.postJSON(url, payload, 'POST', undefined, true);
  }

  async requestContactVerification(payload: {
    source: ContactVerificationSource;
    provisionalPatientId: string;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/register/contact-verification',
    );
    return this.common.postJSON(url, payload);
  }

  async findOrCreateProvisionalFormSet(payload: {
    urlIdentifier: string;
    domain: string;
    source?: string;
  }): Promise<ProvisionalFormSetData> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/register/provisional',
    );
    return this.common.postJSON(url, payload, 'POST', undefined, true);
  }

  async requestMessage(
    phoneNumber: string,
    refCode?: string,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath('/patient-previsit/request-message');
    return this.common.postJSON(url, { phoneNumber, refCode });
  }

  async verifyCodeAndLogin(payload: {
    code: string;
    provisionalPatientId: string;
  }): Promise<{
    authenticationCode: string;
    reconciliationStatus: ReconciliationStatus;
  }> {
    const url = this.common.constructPath(
      '/patient-previsit/auth/register/contact-verification/verify',
    );
    return this.common.postJSON(url, payload);
  }

  async logout() {
    const url = this.common.constructPath(`/patient-previsit/auth/logout`);
    return this.common.postJSON(url, {});
  }

  async getThemeForLogin(args: {
    domain: string;
    authCode?: string;
    registrationCode?: string;
    provisionalAuthCode?: string;
    requestCode?: string;
    encounterId?: string;
  }): Promise<ThemeResponse> {
    const url = this.common.constructPath(`/patient-previsit/theme`, args);
    return this.common.getJSON(url);
  }

  async getScheduleLink(previsitFormSetId: string): Promise<string> {
    const url = this.common.constructPath(`/patient-previsit/schedule-link`, {
      previsitFormSetId,
    });
    const { link } = await this.common.getJSON(url);
    return link;
  }

  async getReceiptInfo(
    authenticationCode: string,
  ): Promise<{ receiptUrl: string; encounterData: EncounterData }> {
    const url = this.common.constructPath(
      `/patient-previsit/receipt/${authenticationCode}/info`,
    );
    return this.common.getJSON(url);
  }

  async createPatientCallbackRequest(
    payload: PatientCallbackRequest.CreateValues,
  ): Promise<PatientCallbackRequest.PatientCallbackRequest> {
    const url = this.common.constructPath(
      '/patient-previsit/patient-callback-requests',
    );
    return this.common.putJSON(url, payload);
  }

  async getIsPatientAuthenticated(configurationId: string): Promise<{
    isAuthenticated: boolean;
  }> {
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/is-authenticated`,
    );
    return this.common.getJSON(url, undefined, true);
  }

  async logoutAssistant(configurationId: string) {
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/logout`,
    );
    return this.common.postJSON(url, {}, 'POST');
  }

  async authPatientForAssistant(args: {
    loginInfo: AuthPatientBody;
    configurationId: string;
    sessionId: string;
  }): Promise<{
    provisionalPatient: ProvisionalPatient.ProvisionalPatient;
    contactVerification: boolean;
  }> {
    const { configurationId, loginInfo, sessionId } = args;
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/login`,
    );
    return this.common.postJSON(
      url,
      { ...loginInfo, sessionId },
      'POST',
      undefined,
      true,
    );
  }

  async verifyAuthPatientForAssistant(args: {
    configurationId: string;
    provisionalPatientId: string;
    code: string;
    sessionId: string;
  }): Promise<{
    success: boolean;
    reconciliationStatus: ReconciliationStatus;
  }> {
    const { configurationId, provisionalPatientId, code, sessionId } = args;
    const url = this.common.constructPath(
      `/patient-previsit/auth/reconciliation/assistants/${configurationId}/patients/verify`,
    );
    return this.common.postJSON(
      url,
      { provisionalPatientId, code, sessionId },
      'POST',
    );
  }

  async generateConsentLink(args: { encounterId: string }): Promise<string> {
    const url = this.common.constructPath('/patient-previsit/musc/helix-login');
    const { consentLink } = await this.common.postJSON(url, args);
    return consentLink;
  }

  async findHistory(
    formTemplateId: string,
  ): Promise<PrevisitFormTemplate.PrevisitFormTemplateHistory[]> {
    const url = this.common.constructPath(
      `/previsit/form-templates/${formTemplateId}/history`,
    );
    const { history } = await this.common.getJSON(url);
    return history;
  }

  async checkIn(args: { previsitFormSetId: string }): Promise<any> {
    const url = this.common.constructPath(
      `/patient-previsit/front-desk/check-in`,
    );
    return this.common.postJSON(url, args);
  }

  async fetchDigitalCheckInDetails({
    authCode,
    encounterId,
  }: {
    authCode: string;
    encounterId: string;
  }): Promise<DigitalCheckIn.DigitalCheckInDetails> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/${encounterId}/digital-check-in`,
      {
        authCode,
      },
    );

    return this.common.getJSON(url);
  }

  async digitalCheckInEncounter({
    encounterId,
    authCode,
  }: {
    encounterId: string;
    authCode: string;
  }): Promise<void> {
    const url = this.common.constructPath(
      `/patient-previsit/encounter/${encounterId}/digital-check-in`,
    );

    return this.common.postJSON(url, { authCode });
  }
}
